import { useGlobalMutation } from 'state/query-client/hooks';
import { createIntegration, CreateIntegrationRequest, Integration } from 'services/integration-service';
import { useConfig } from './use-config';
import { useIntegrationsStore } from 'state/store/integrations/useIntegrationsStore';

/**
 * Custom hook to create integrations for external service connections
 *
 * This hook provides a mutation function to create integrations after successful
 * connections to external services like Google Drive. It uses the global mutation
 * pattern for consistent error handling and authentication management.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Loading state management
 * - TypeScript support for request/response types
 *
 * @returns {Object} The mutation object with methods and state
 * @returns {Function} mutate - Function to trigger the integration creation
 * @returns {Function} mutateAsync - Async function to trigger the integration creation
 * @returns {boolean} isPending - Whether the mutation is in progress
 * @returns {boolean} isSuccess - Whether the mutation completed successfully
 * @returns {boolean} isError - Whether the mutation failed
 * @returns {Integration | undefined} data - The created integration data
 * @returns {Error | null} error - The error if mutation failed
 *
 * @example
 * const { mutate: createIntegration, isPending } = useCreateIntegration();
 * 
 * const handleConnectionSuccess = (connection) => {
 *   createIntegration({
 *     connection_id: connection.id,
 *     scope: "user",
 *     integration_type: "google_drive"
 *   }, {
 *     onSuccess: (data) => {
 *       console.log('Integration created:', data);
 *     },
 *     onError: (error) => {
 *       console.error('Failed to create integration:', error);
 *     }
 *   });
 * };
 */
export const useCreateIntegration = () => {
    const { configurationSettings } = useConfig();
    const { addIntegration } = useIntegrationsStore();

    return useGlobalMutation<Integration, Error, CreateIntegrationRequest>({
        mutationFn: async (payload: CreateIntegrationRequest) => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            const result = await createIntegration(configurationSettings, payload);
            // Add the new integration to the store
            addIntegration(result);
            return result;
        },
    });
};
